/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React from 'react';
import { IconBox, LinkButton, Tag, Tone } from '../console/ui';
import { ExternalIc } from '../console/icons';

export interface ServiceAction {
  label: string;
  to: string;
  primary?: boolean;
}

export interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  tag?: { tone: Tone; label: React.ReactNode } | null;
  facts: Array<{ k: string; v: React.ReactNode; mono?: boolean }>;
  actions: ServiceAction[];
}

const isExternal = (to: string) => /^https?:/.test(to);

/** Service card on the All services hub: icon, status, facts and actions. */
const ServiceCard: React.FC<ServiceCardProps> = ({ icon, title, description, tag, facts, actions }) => (
  <article className="ps-card p-5 flex flex-col gap-3.5 min-w-0">
    <div className="flex gap-3.5 items-start">
      <IconBox className="[&>svg]:w-5 [&>svg]:h-5">{icon}</IconBox>
      <div className="min-w-0 flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="m-0 text-base font-semibold">{title}</h3>
          {tag && <Tag tone={tag.tone}>{tag.label}</Tag>}
        </div>
        <p className="m-0 text-[13px] text-ps-muted leading-relaxed">{description}</p>
      </div>
    </div>
    {facts.length > 0 && (
      <dl className="m-0">
        {facts.map((f) => (
          <div key={f.k} className="flex justify-between gap-3 text-[13px] py-1.5 border-t border-ps-raised">
            <dt className="text-ps-subtle flex-none">{f.k}</dt>
            <dd className={`m-0 text-right min-w-0 break-words ${f.mono ? 'font-mono' : ''}`}>{f.v}</dd>
          </div>
        ))}
      </dl>
    )}
    <div className="mt-auto flex flex-wrap gap-2.5 max-[820px]:[&>*]:flex-1 max-[820px]:[&>*]:min-h-[44px]">
      {actions.map((a) => (
        <LinkButton key={a.label} to={a.to} variant={a.primary ? 'primary' : 'secondary'}>
          {a.label}
          {isExternal(a.to) && <ExternalIc className="w-3.5 h-3.5" aria-hidden="true" />}
        </LinkButton>
      ))}
    </div>
  </article>
);

export default ServiceCard;
